/**
 * Erzeugt die GTM-Variante eines Event-Objekts: `value`/`price` übernehmen die
 * Netto-Werte, die Netto-Hilfsfelder werden entfernt.
 * @param {Object} eventObject - Das normalisierte Event-Objekt
 * @returns {Object} Deep-Clone mit Netto-Werten für GTM
 */
export function applyNettoForGtm(eventObject) {
    const clone = deepClone(eventObject);
    const eventParams = getEventParams(clone);

    applyNettoValue(eventParams);

    if (Array.isArray(eventParams.items)) {
        for (const item of eventParams.items) {
            applyNettoPrice(item);
        }
    }

    return clone;
}

/**
 * Erzeugt die GA4-Variante eines Event-Objekts: `value`/`price` bleiben
 * unverändert (Brutto), nur die Netto-Hilfsfelder werden entfernt.
 * @param {Object} eventObject - Das normalisierte Event-Objekt
 * @returns {Object} Deep-Clone ohne Netto-Hilfsfelder
 */
export function stripNettoFields(eventObject) {
    const clone = deepClone(eventObject);
    const eventParams = getEventParams(clone);

    delete eventParams.value_netto;

    if (Array.isArray(eventParams.items)) {
        for (const item of eventParams.items) {
            if (item && typeof item === 'object') {
                delete item.price_netto;
            }
        }
    }

    return clone;
}

/**
 * Liefert das Objekt mit den Event-Parametern: `ecommerce` bei Ecommerce-Events,
 * sonst das Event-Objekt selbst (oberste Ebene).
 * @param {Object} eventObject
 * @returns {Object}
 */
function getEventParams(eventObject) {
    if (eventObject.ecommerce && typeof eventObject.ecommerce === 'object') {
        return eventObject.ecommerce;
    }
    return eventObject;
}

/**
 * Übernimmt `value_netto` in `value` (falls vorhanden) und entfernt `value_netto`.
 * @param {Object} params
 */
function applyNettoValue(params) {
    if (hasNettoValue(params.value_netto)) {
        params.value = params.value_netto;
    }
    delete params.value_netto;
}

/**
 * Übernimmt `price_netto` in `price` (falls vorhanden) und entfernt `price_netto`.
 * @param {Object} item
 */
function applyNettoPrice(item) {
    if (!item || typeof item !== 'object') {
        return;
    }
    if (hasNettoValue(item.price_netto)) {
        item.price = item.price_netto;
    }
    delete item.price_netto;
}

/**
 * Prüft, ob ein Netto-Wert vorhanden ist. Ein leerer String zählt als
 * "nicht vorhanden" — sonst würde ein leeres `price_netto`/`value_netto`
 * den gültigen Brutto-Wert überschreiben (z.B. bei Marketplace-Artikeln
 * ohne Netto-Preis).
 * @param {*} value
 * @returns {boolean}
 */
function hasNettoValue(value) {
    return value !== undefined && value !== null && value !== '';
}

/**
 * Erstellt einen Deep-Clone eines reinen Daten-Objekts (JSON-serialisierbar).
 * @param {Object} value
 * @returns {Object}
 */
function deepClone(value) {
    return JSON.parse(JSON.stringify(value));
}
